import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Dashboard from '../components/editor/Dashboard';
import TeamEditor from '../components/editor/TeamEditor';
import EventsEditor from '../components/editor/EventsEditor';
import CharityEditor from '../components/editor/CharityEditor';
import StatisticsEditor from '../components/editor/StatisticsEditor';
import '../styles/editor.css';

const API_BASE = 'https://canopy-content-auth.dthoma5s-account.workers.dev';

function NewEditor() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [toast, setToast] = useState(null);

  // Content state
  const [teamData, setTeamData] = useState(null);
  const [eventsData, setEventsData] = useState(null);
  const [charityData, setCharityData] = useState(null);
  const [statisticsData, setStatisticsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  // Check for existing auth token
  useEffect(() => {
    const token = localStorage.getItem('canopy_auth_token');
    if (token) {
      verifyToken(token);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/verify`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setAuthToken(token);
        setIsAuthenticated(true);
        loadAllData(token);
      } else {
        localStorage.removeItem('canopy_auth_token');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('canopy_auth_token');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');

    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const data = await response.json();

      if (response.ok && data.token) {
        localStorage.setItem('canopy_auth_token', data.token);
        setAuthToken(data.token);
        setIsAuthenticated(true);
        loadAllData(data.token);
      } else {
        setLoginError(data.error || 'Login failed');
      }
    } catch (error) {
      setLoginError('Connection error. Please try again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('canopy_auth_token');
    setAuthToken(null);
    setIsAuthenticated(false);
    setTeamData(null);
    setEventsData(null);
    setCharityData(null);
    setStatisticsData(null);
  };

  const loadAllData = async (token) => {
    setLoading(true);
    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [teamRes, eventsRes, charityRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/content/team`, { headers }),
        fetch(`${API_BASE}/api/content/events`, { headers }),
        fetch(`${API_BASE}/api/content/charity`, { headers }),
        fetch(`${API_BASE}/api/content/statistics`, { headers })
      ]);

      if (teamRes.ok) {
        const team = await teamRes.json();
        setTeamData(team.data || team);
      }
      if (eventsRes.ok) {
        const events = await eventsRes.json();
        setEventsData(events.data || events);
      }
      if (charityRes.ok) {
        const charity = await charityRes.json();
        setCharityData(charity.data || charity);
      }
      if (statsRes.ok) {
        const stats = await statsRes.json();
        setStatisticsData(stats.data || stats);
      }

      setLastSync(new Date().toISOString());
    } catch (error) {
      console.error('Failed to load data:', error);
      showToast('Failed to load content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const saveContent = async (contentType, data) => {
    try {
      const response = await fetch(`${API_BASE}/api/content/${contentType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (response.ok) {
        showToast(`${contentType.charAt(0).toUpperCase() + contentType.slice(1)} saved successfully`);
        setLastSync(new Date().toISOString());

        // Update local state
        switch (contentType) {
          case 'team':
            setTeamData(data);
            break;
          case 'events':
            setEventsData(data);
            break;
          case 'charity':
            setCharityData(data);
            break;
          case 'statistics':
            setStatisticsData(data);
            break;
          default:
            break;
        }

        return { success: true, data: result };
      } else {
        showToast(result.error || 'Save failed', 'error');
        return { success: false, error: result.error };
      }
    } catch (error) {
      showToast('Connection error', 'error');
      return { success: false, error: error.message };
    }
  };

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="editor-container">
        <div className="login-container">
          <h1 className="login-title">Content Manager</h1>
          <form className="login-form" onSubmit={handleLogin}>
            <input
              type="text"
              className="login-input"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
            <input
              type="password"
              className="login-input"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
            {loginError && <div className="login-error">{loginError}</div>}
            <button type="submit" className="btn-editor btn-editor-primary" style={{ width: '100%' }}>
              Sign In
            </button>
          </form>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'team', label: 'Team' },
    { id: 'events', label: 'Events' },
    { id: 'charity', label: 'Charity' },
    { id: 'statistics', label: 'Statistics' }
  ];

  const getTotalMembers = () => {
    if (!teamData) return 0;
    return (teamData.founder?.length || 0) +
      (teamData.directors?.length || 0) +
      (teamData.advisors?.length || 0) +
      (teamData.members?.length || 0);
  };

  return (
    <div className="editor-container">
      {/* Header */}
      <div className="editor-header">
        <div>
          <h1 className="editor-title">Content Manager</h1>
          <p className="editor-subtitle">Manage team, events, and content</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          {lastSync && (
            <div className="sync-status">
              <span className="sync-status-dot"></span>
              Last synced: {new Date(lastSync).toLocaleTimeString()}
            </div>
          )}
          <button onClick={handleLogout} className="btn-editor btn-editor-secondary">
            Sign Out
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <nav className="editor-nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`editor-nav-item ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        <Link to="/manage/legacy" className="legacy-link" style={{ marginLeft: 'auto' }}>
          Legacy Editor
        </Link>
      </nav>

      {/* Content Area */}
      {loading ? (
        <div className="empty-state">
          <div className="empty-state-icon">...</div>
          <div className="empty-state-title">Loading content...</div>
        </div>
      ) : (
        <>
          {activeTab === 'dashboard' && (
            <Dashboard
              teamCount={getTotalMembers()}
              eventsCount={eventsData?.length || 0}
              charityCount={charityData?.length || 0}
              onNavigate={setActiveTab}
            />
          )}
          {activeTab === 'team' && (
            <TeamEditor
              data={teamData}
              onSave={(data) => saveContent('team', data)}
              showToast={showToast}
            />
          )}
          {activeTab === 'events' && (
            <EventsEditor
              data={eventsData}
              onSave={(data) => saveContent('events', data)}
              showToast={showToast}
            />
          )}
          {activeTab === 'charity' && (
            <CharityEditor
              data={charityData}
              onSave={(data) => saveContent('charity', data)}
              showToast={showToast}
            />
          )}
          {activeTab === 'statistics' && (
            <StatisticsEditor
              data={statisticsData}
              onSave={(data) => saveContent('statistics', data)}
              showToast={showToast}
            />
          )}
        </>
      )}

      {/* Toast Notification */}
      {toast && (
        <div className={`toast ${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

export default NewEditor;
